import * as THREE from 'three';
import { configureStableTextureSampling, syncCanvasTextureClones } from './texture-sampling';

export interface TextCanvasOptions {
  text: string;
  fontSize: number;
  fontFamily: string;
  fontUrl: string | null;
  color: string;
  backgroundColor: string | null;
  align: 'left' | 'center' | 'right';
  padding: number;
  width: number;
  height: number;
  bold: boolean;
  italic: boolean;
  blur: number;
  superSample: number;

  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  letterSpacing: number;
  wordSpacing: number;
  lineHeight: number;
  verticalAlign: 'top' | 'middle' | 'bottom';
  direction: 'horizontal' | 'vertical';
  textOpacity: number;

  strokeEnabled: boolean;
  strokeColor: string;
  strokeWidth: number;

  stroke2Enabled: boolean;
  stroke2Color: string;
  stroke2Width: number;

  shadowEnabled: boolean;
  shadowColor: string;
  shadowBlur: number;
  shadowOffsetX: number;
  shadowOffsetY: number;
  shadowOpacity: number;
}

const DEFAULT_OPTIONS: TextCanvasOptions = {
  text: '',
  fontSize: 48,
  fontFamily: 'Arial',
  fontUrl: null,
  color: '#ffffff',
  backgroundColor: null,
  align: 'center',
  padding: 16,
  width: 512,
  height: 512,
  bold: false,
  italic: false,
  blur: 0,
  superSample: 2,

  textTransform: 'none',
  letterSpacing: 0,
  wordSpacing: 0,
  lineHeight: 1.25,
  verticalAlign: 'middle',
  direction: 'horizontal',
  textOpacity: 1,

  strokeEnabled: false,
  strokeColor: '#000000',
  strokeWidth: 2,

  stroke2Enabled: false,
  stroke2Color: '#000000',
  stroke2Width: 4,

  shadowEnabled: false,
  shadowColor: '#000000',
  shadowBlur: 4,
  shadowOffsetX: 2,
  shadowOffsetY: 2,
  shadowOpacity: 0.7,
};

const fontCache = new Map<string, Promise<boolean>>();
/** Live FontFace handles so we can drop the previous script font (CJK OTF is ~12MB+). */
const loadedFontFaces = new Map<string, FontFace>();

function fontFormatFromUrl(url: string): 'opentype' | 'truetype' | 'woff' | 'woff2' | undefined {
  const path = url.split('?')[0].split('#')[0].toLowerCase();
  if (path.endsWith('.otf')) return 'opentype';
  if (path.endsWith('.ttf')) return 'truetype';
  if (path.endsWith('.woff2')) return 'woff2';
  if (path.endsWith('.woff')) return 'woff';
  return undefined;
}

function unloadOtherTextFonts(keepKey: string) {
  for (const [key, face] of [...loadedFontFaces.entries()]) {
    if (key === keepKey) continue;
    try {
      document.fonts.delete(face);
    } catch {
      /* ignore */
    }
    loadedFontFaces.delete(key);
    fontCache.delete(key);
  }
}

function hasHanScript(text: string): boolean {
  try {
    return /\p{Script=Han}/u.test(text);
  } catch {
    return /[\u3400-\u9FFF\uF900-\uFAFF]/.test(text);
  }
}

/**
 * Safari/WebKit often reports FontFace loaded for CJK OTFs but canvas still paints
 * the system fallback until glyphs are explicitly requested — attaching a Latin
 * letter was the workaround. Force glyph residency for the actual sample string.
 */
async function waitForFontGlyphs(family: string, sampleText: string): Promise<void> {
  const probe =
    sampleText.trim() ||
    (hasHanScript(sampleText) ? sampleText : '中文Aa');
  const spec = `32px "${family}"`;
  try {
    await document.fonts.load(spec, probe);
  } catch {
    /* ignore */
  }
  const han = probe.match(/[\u3400-\u9FFF\uF900-\uFAFF]/)?.[0];
  if (!han) return;
  for (let i = 0; i < 12; i++) {
    try {
      if (document.fonts.check(spec, han)) return;
      await document.fonts.load(spec, han + '汉');
    } catch {
      /* ignore */
    }
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
  }
}

function loadFont(family: string, url: string): Promise<boolean> {
  const key = `${family}::${url}`;
  if (fontCache.has(key)) return fontCache.get(key)!;

  const promise = (async () => {
    const format = fontFormatFromUrl(url);
    const descriptors = format
      ? ({ format } as FontFaceDescriptors & { format: string })
      : undefined;

    const tryLoad = async (source: string | ArrayBuffer) => {
      const face = descriptors
        ? new FontFace(family, source, descriptors)
        : new FontFace(family, source);
      const loaded = await face.load();
      unloadOtherTextFonts(key);
      document.fonts.add(loaded);
      loadedFontFaces.set(key, loaded);
      await document.fonts.ready;
      return true;
    };

    // iOS: url()-loaded CJK OTFs often don't bind to canvas until a Latin glyph is
    // drawn. ArrayBuffer registration is reliable for simkai-sized faces.
    const isIos =
      typeof navigator !== 'undefined' &&
      (/iphone|ipad|ipod/i.test(navigator.userAgent) ||
        (/\bMac\b/i.test(navigator.userAgent) && 'ontouchend' in document));
    const looksCjkFace = /kai|song|hei|cjk|noto.?sans.?sc|source.?han|simsun|simkai|zh/i.test(
      `${family} ${url}`,
    );

    const loadViaBuffer = async () => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const buffer = await res.arrayBuffer();
      return tryLoad(buffer);
    };

    try {
      if (isIos || looksCjkFace) {
        try {
          return await loadViaBuffer();
        } catch {
          return await tryLoad(`url(${JSON.stringify(url)})`);
        }
      }
      return await tryLoad(`url(${JSON.stringify(url)})`);
    } catch (err) {
      try {
        return await loadViaBuffer();
      } catch (err2) {
        console.warn(`[TextCanvas] Failed to load font "${family}" from ${url.slice(0, 80)}:`, err2 ?? err);
        fontCache.delete(key);
        return false;
      }
    }
  })();

  fontCache.set(key, promise);
  return promise;
}

/** Notify material-graph CPU bakers + UV clones that canvas pixels changed. */
function markTextCanvasContentReady(tex: THREE.CanvasTexture): void {
  const ud = tex.userData as Record<string, unknown>;
  delete ud.__c3dBakePending;
  ud.__c3dContentEpoch = ((ud.__c3dContentEpoch as number) || 0) + 1;
  tex.needsUpdate = true;
  // Engraving opacity/bump are UV-cloned; clones kept the blank pre-font canvas otherwise.
  syncCanvasTextureClones(tex);
  if (typeof document !== 'undefined') {
    document.dispatchEvent(new CustomEvent('scene-texture-ready'));
  }
}

export function createTextCanvasTexture(
  opts: Partial<TextCanvasOptions>,
): THREE.CanvasTexture {
  const o = { ...DEFAULT_OPTIONS, ...opts };

  // CJK: hold blank + bake-pending until the face binds (Safari stamps Latin fallback
  // otherwise). Latin: paint immediately so engraving stays visible even before load.
  const deferPaint = Boolean(o.fontUrl && hasHanScript(o.text));
  const tex = deferPaint
    ? renderToCanvas(null, { ...o, text: '' })
    : renderToCanvas(null, o);

  if (o.fontUrl) {
    if (deferPaint) {
      (tex.userData as Record<string, unknown>).__c3dBakePending = true;
    }
    const family = o.fontFamily;
    const url = o.fontUrl;
    const sample = o.text;
    loadFont(family, url)
      .then(async (ok) => {
        if (ok) await waitForFontGlyphs(family, sample);
        renderToCanvas(tex, o);
        markTextCanvasContentReady(tex);
      })
      .catch(() => {
        renderToCanvas(tex, o);
        markTextCanvasContentReady(tex);
      });
  }

  return tex;
}

function applyTextTransform(text: string, transform: string): string {
  // Uppercase/lowercase are Latin-oriented; leave CJK / mixed script alone.
  if (hasHanScript(text) && (transform === 'uppercase' || transform === 'lowercase' || transform === 'capitalize')) {
    return text;
  }
  switch (transform) {
    case 'uppercase': return text.toUpperCase();
    case 'lowercase': return text.toLowerCase();
    case 'capitalize': return text.replace(/\b\w/g, (c) => c.toUpperCase());
    default: return text;
  }
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function setupFont(ctx: CanvasRenderingContext2D, o: TextCanvasOptions, ss: number) {
  const scaledFontSize = o.fontSize * ss;
  const weight = o.bold ? 'bold' : 'normal';
  const style = o.italic ? 'italic' : 'normal';
  ctx.font = `${style} ${weight} ${scaledFontSize}px "${o.fontFamily}"`;
  ctx.textBaseline = 'middle';

  if (o.letterSpacing !== 0 && 'letterSpacing' in ctx) {
    (ctx as any).letterSpacing = `${o.letterSpacing * ss}px`;
  }
  if (o.wordSpacing !== 0 && 'wordSpacing' in ctx) {
    (ctx as any).wordSpacing = `${o.wordSpacing * ss}px`;
  }
}

function drawTextLines(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  anchorX: number,
  startY: number,
  lineH: number,
  maxW: number,
  o: TextCanvasOptions,
  ss: number,
  mode: 'fill' | 'stroke',
) {
  for (let i = 0; i < lines.length; i++) {
    const y = startY + i * lineH;
    if (mode === 'stroke') {
      ctx.strokeText(lines[i], anchorX, y, maxW);
    } else {
      ctx.fillText(lines[i], anchorX, y, maxW);
    }
  }
}

/**
 * Vertical layout uses a fixed step per "slot". With textBaseline 'middle', punctuation
 * (e.g. middle dot ·) often sits optically high/low vs caps because em-box center ≠ ink center.
 * Using alphabetic baseline + actualBoundingBox* centers each glyph's painted bounds in its slot.
 */
function verticalSlotBaselineY(ctx: CanvasRenderingContext2D, ch: string, slotCenterY: number): number {
  const prevBaseline = ctx.textBaseline;
  ctx.textBaseline = 'alphabetic';
  const m = ctx.measureText(ch);
  const a = m.actualBoundingBoxAscent;
  const d = m.actualBoundingBoxDescent;
  ctx.textBaseline = prevBaseline;
  if (
    typeof a === 'number' &&
    typeof d === 'number' &&
    Number.isFinite(a) &&
    Number.isFinite(d) &&
    a + d > 0
  ) {
    // Ink vertical center = baselineY + (d - a) / 2  →  baselineY = slotCenterY - (d - a) / 2
    return slotCenterY - (d - a) / 2;
  }
  return slotCenterY;
}

function drawVerticalText(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  startY: number,
  charH: number,
  mode: 'fill' | 'stroke',
) {
  const chars = [...text];
  const savedBaseline = ctx.textBaseline;
  ctx.textBaseline = 'alphabetic';
  for (let i = 0; i < chars.length; i++) {
    const slotCenterY = startY + i * charH;
    const baselineY = verticalSlotBaselineY(ctx, chars[i], slotCenterY);
    if (mode === 'stroke') {
      ctx.strokeText(chars[i], cx, baselineY);
    } else {
      ctx.fillText(chars[i], cx, baselineY);
    }
  }
  ctx.textBaseline = savedBaseline;
}

function renderToCanvas(
  existingTex: THREE.CanvasTexture | null,
  o: TextCanvasOptions,
): THREE.CanvasTexture {
  const ss = Math.max(1, Math.min(4, Math.round(o.superSample)));
  const rw = o.width * ss;
  const rh = o.height * ss;

  const canvas = document.createElement('canvas');
  canvas.width = rw;
  canvas.height = rh;
  const ctx = canvas.getContext('2d')!;

  if (o.backgroundColor) {
    ctx.fillStyle = o.backgroundColor;
    ctx.fillRect(0, 0, rw, rh);
  } else {
    ctx.clearRect(0, 0, rw, rh);
  }

  const displayText = applyTextTransform(o.text, o.textTransform);
  const scaledPadding = o.padding * ss;
  const scaledFontSize = o.fontSize * ss;
  const lineH = scaledFontSize * o.lineHeight;
  const maxW = rw - scaledPadding * 2;

  setupFont(ctx, o, ss);
  ctx.textAlign = o.align;

  const hasStroke = o.strokeEnabled && o.strokeWidth > 0;
  const hasStroke2 = o.stroke2Enabled && o.stroke2Width > 0;
  const hasShadow = o.shadowEnabled;
  const needsTextLayer = hasShadow;

  let targetCtx = ctx;
  let textLayerCanvas: HTMLCanvasElement | null = null;

  if (needsTextLayer) {
    textLayerCanvas = document.createElement('canvas');
    textLayerCanvas.width = rw;
    textLayerCanvas.height = rh;
    targetCtx = textLayerCanvas.getContext('2d')!;
    setupFont(targetCtx, o, ss);
    targetCtx.textAlign = o.align;
    targetCtx.textBaseline = 'middle';
  }

  if (o.direction === 'vertical') {
    const chars = [...displayText.replace(/\n/g, '')];
    const charH = scaledFontSize * o.lineHeight;
    const totalH = chars.length * charH;
    const cx = rw / 2;

    let startY: number;
    if (o.verticalAlign === 'top') startY = scaledPadding + charH / 2;
    else if (o.verticalAlign === 'bottom') startY = rh - scaledPadding - totalH + charH / 2;
    else startY = (rh - totalH) / 2 + charH / 2;

    targetCtx.textAlign = 'center';

    if (hasStroke2) {
      targetCtx.strokeStyle = o.stroke2Color;
      targetCtx.lineWidth = o.stroke2Width * ss * 2;
      targetCtx.lineJoin = 'round';
      targetCtx.lineCap = 'round';
      drawVerticalText(targetCtx, displayText.replace(/\n/g, ''), cx, startY, charH, 'stroke');
    }
    if (hasStroke) {
      targetCtx.strokeStyle = o.strokeColor;
      targetCtx.lineWidth = o.strokeWidth * ss * 2;
      targetCtx.lineJoin = 'round';
      targetCtx.lineCap = 'round';
      drawVerticalText(targetCtx, displayText.replace(/\n/g, ''), cx, startY, charH, 'stroke');
    }

    targetCtx.globalAlpha = o.textOpacity;
    targetCtx.fillStyle = o.color;
    drawVerticalText(targetCtx, displayText.replace(/\n/g, ''), cx, startY, charH, 'fill');
    targetCtx.globalAlpha = 1;
  } else {
    const lines = wrapText(ctx, displayText, maxW);
    const totalH = lines.length * lineH;

    let startY: number;
    if (o.verticalAlign === 'top') startY = scaledPadding + lineH / 2;
    else if (o.verticalAlign === 'bottom') startY = rh - scaledPadding - totalH + lineH / 2;
    else startY = (rh - totalH) / 2 + lineH / 2;

    let anchorX: number;
    if (o.align === 'left') anchorX = scaledPadding;
    else if (o.align === 'right') anchorX = rw - scaledPadding;
    else anchorX = rw / 2;

    if (hasStroke2) {
      targetCtx.strokeStyle = o.stroke2Color;
      targetCtx.lineWidth = o.stroke2Width * ss * 2;
      targetCtx.lineJoin = 'round';
      targetCtx.lineCap = 'round';
      drawTextLines(targetCtx, lines, anchorX, startY, lineH, maxW, o, ss, 'stroke');
    }
    if (hasStroke) {
      targetCtx.strokeStyle = o.strokeColor;
      targetCtx.lineWidth = o.strokeWidth * ss * 2;
      targetCtx.lineJoin = 'round';
      targetCtx.lineCap = 'round';
      drawTextLines(targetCtx, lines, anchorX, startY, lineH, maxW, o, ss, 'stroke');
    }

    targetCtx.globalAlpha = o.textOpacity;
    targetCtx.fillStyle = o.color;
    drawTextLines(targetCtx, lines, anchorX, startY, lineH, maxW, o, ss, 'fill');
    targetCtx.globalAlpha = 1;
  }

  if (needsTextLayer && textLayerCanvas) {
    const shadowAlpha = Math.max(0, Math.min(1, o.shadowOpacity));
    ctx.shadowColor = hexToRgba(o.shadowColor, shadowAlpha);
    ctx.shadowBlur = o.shadowBlur * ss;
    ctx.shadowOffsetX = o.shadowOffsetX * ss;
    ctx.shadowOffsetY = o.shadowOffsetY * ss;
    ctx.drawImage(textLayerCanvas, 0, 0);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }

  if (o.blur > 0) {
    const blurPx = o.blur * ss;
    const tmp = document.createElement('canvas');
    tmp.width = rw;
    tmp.height = rh;
    const tmpCtx = tmp.getContext('2d')!;
    tmpCtx.filter = `blur(${blurPx}px)`;
    tmpCtx.drawImage(canvas, 0, 0);
    ctx.clearRect(0, 0, rw, rh);
    if (o.backgroundColor) {
      ctx.fillStyle = o.backgroundColor;
      ctx.fillRect(0, 0, rw, rh);
    }
    ctx.drawImage(tmp, 0, 0);
  }

  let finalCanvas: HTMLCanvasElement;
  if (ss > 1) {
    finalCanvas = document.createElement('canvas');
    finalCanvas.width = o.width;
    finalCanvas.height = o.height;
    const outCtx = finalCanvas.getContext('2d')!;
    outCtx.imageSmoothingEnabled = true;
    outCtx.imageSmoothingQuality = 'high';
    outCtx.drawImage(canvas, 0, 0, o.width, o.height);
  } else {
    finalCanvas = canvas;
  }

  if (existingTex) {
    // Prefer mutating the same canvas element so UV Transform clones that share
    // `image` see the new glyphs without a separate image pointer swap.
    const prev = existingTex.image;
    if (
      prev instanceof HTMLCanvasElement &&
      prev.width === finalCanvas.width &&
      prev.height === finalCanvas.height
    ) {
      const pctx = prev.getContext('2d');
      if (pctx) {
        pctx.clearRect(0, 0, prev.width, prev.height);
        pctx.drawImage(finalCanvas, 0, 0);
      } else {
        existingTex.image = finalCanvas;
      }
    } else {
      existingTex.image = finalCanvas;
    }
    configureStableTextureSampling(existingTex);
    return existingTex;
  }

  const tex = new THREE.CanvasTexture(finalCanvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.flipY = false;
  configureStableTextureSampling(tex);
  return tex;
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  if (!text) return [''];
  const paragraphs = text.split('\n');
  const result: string[] = [];

  for (const para of paragraphs) {
    const words = para.split(/\s+/);
    let line = '';
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        result.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    result.push(line);
  }
  return result;
}
